import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { HiddenWhenSpiffOnly } from "@/core/components/hidden-when-spiff-only";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { TEXT_MESSAGE_TAB_LABELS } from "@/text-messages/constants";
import { CONVERSATION_TYPES } from "@/text-messages/constants";
import {
  useGetInboundTextMessages,
  useGetOptedOutTextMessages,
  useGetOutboundTextMessages,
} from "@/text-messages/query";
import { ConversationMessages } from "./text-message-tabs/conversation-messages";
import { OptedOutMessages } from "./text-message-tabs/opted-out-messages";
import { OutboundMessages } from "./text-message-tabs/outbound-messages";

type TextMessageTab = keyof typeof CONVERSATION_TYPES;

const TextMessagesContent = () => {
  const { businessId } = useParams();
  const [activeTab, setActiveTab] = useState<TextMessageTab>("conversation");

  const { data: inboundMessages, isLoading: isLoadingInbound } =
    useGetInboundTextMessages(businessId);

  const { data: outboundMessages, isLoading: isLoadingOutbound } =
    useGetOutboundTextMessages(businessId);

  const { data: optedOutMessages, isLoading: isLoadingOptedOut } =
    useGetOptedOutTextMessages(businessId);

  const messageCounts = useMemo(
    () => ({
      inbound: inboundMessages?.length || 0,
      outbound: outboundMessages?.length || 0,
      opted_out: optedOutMessages?.length || 0,
    }),
    [inboundMessages, outboundMessages, optedOutMessages],
  );

  return (
    <Stack className="mr-5" gap="sm" fullWidth>
      <HeaderStack>
        <PageHeader>Text Messages</PageHeader>
      </HeaderStack>
      <MainCard>
        <MainStack>
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TextMessageTab)}
            className="flex w-full flex-1 flex-col overflow-hidden"
          >
            <TabsListWithIndicator
              className="w-full justify-start"
              containerClassName="mr-5"
            >
              <TabsTrigger
                value={CONVERSATION_TYPES.conversation}
                count={messageCounts.inbound}
                isCountLoading={isLoadingInbound}
              >
                {TEXT_MESSAGE_TAB_LABELS[CONVERSATION_TYPES.conversation]}
              </TabsTrigger>
              <TabsTrigger
                value={CONVERSATION_TYPES.outbound}
                count={messageCounts.outbound}
                isCountLoading={isLoadingOutbound}
              >
                {TEXT_MESSAGE_TAB_LABELS[CONVERSATION_TYPES.outbound]}
              </TabsTrigger>
              <TabsTrigger
                value={CONVERSATION_TYPES.opted_out}
                count={messageCounts.opted_out}
                isCountLoading={isLoadingOptedOut}
              >
                {TEXT_MESSAGE_TAB_LABELS[CONVERSATION_TYPES.opted_out]}
              </TabsTrigger>
            </TabsListWithIndicator>
            <TabsContent
              value={CONVERSATION_TYPES.conversation}
              className="flex-1 overflow-hidden pt-4"
            >
              <ConversationMessages />
            </TabsContent>
            <TabsContent
              value={CONVERSATION_TYPES.outbound}
              className="flex-1 overflow-hidden pt-4"
            >
              <OutboundMessages />
            </TabsContent>
            <TabsContent
              value={CONVERSATION_TYPES.opted_out}
              className="flex-1 overflow-hidden pt-4"
            >
              <OptedOutMessages />
            </TabsContent>
          </Tabs>
        </MainStack>
      </MainCard>
    </Stack>
  );
};

const TextMessages = () => (
  <HiddenWhenSpiffOnly fallbackPath="../">
    <TextMessagesContent />
  </HiddenWhenSpiffOnly>
);

export default TextMessages;
